const ALLOWED_HTML_ELEMENTS = new Set([
  'html',
  'head',
  'body',
  'header',
  'footer',
  'main',
  'section',
  'article',
  'nav',
  'aside',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'div',
  'span',
  'pre',
  'blockquote',
  'hr',
  'ul',
  'ol',
  'li',
  'dl',
  'dt',
  'dd',
  'strong',
  'em',
  'b',
  'i',
  'u',
  's',
  'small',
  'mark',
  'sub',
  'sup',
  'abbr',
  'cite',
  'code',
  'kbd',
  'samp',
  'var',
  'time',
  'q',
  'br',
  'wbr',
  'table',
  'caption',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
  'colgroup',
  'col',
  'label',
  'fieldset',
  'legend',
  'ruby',
  'rb',
  'rt',
  'rtc',
  'rp',
  'ins',
  'del',
  'center',
  'big',
  'strike',
]);

/**
 * SECURITY NOTE
 *
 * These helpers intentionally parse HTML into a *detached* element via `innerHTML`.
 * The element is never attached to the live document, so resource loading and
 * associated events (e.g. <img onerror>, <video onloadstart>, etc.) are not
 * triggered during parsing.
 *
 * Some automated reviews flag `innerHTML = userInput` as “scripts/events execute
 * immediately before sanitization”. That is not accurate for this usage pattern:
 * - `<script>` inserted via `innerHTML` is not executed by design.
 * - Event handlers only fire when elements are connected and the browser loads
 *   external resources, which we do not do here.
 *
 * IMPORTANT:
 * - Do not attach the intermediate `virtualDOM` to the document.
 * - Only use the *returned* string after it has been passed through `sanitizeHtml`
 *   (or the specific helper you need).
 * - Switching to `DOMParser` does not inherently make untrusted HTML “safe” and
 *   is not a substitute for sanitization.
 */
export function removeInlineStyles(html) {
  const virtualDOM = document.createElement('div');
  virtualDOM.innerHTML = html;

  // Find all elements with style attributes and remove them
  const elementsWithStyle = virtualDOM.querySelectorAll('[style]');
  for (let i = 0; i < elementsWithStyle.length; i++) {
    elementsWithStyle[i].removeAttribute('style');
  }

  return virtualDOM.innerHTML;
}

/**
 * Remove all inline event handler attributes from the provided HTML.
 * Example: onclick, onerror, onload, etc.
 *
 * @param {string} html
 * @returns {string}
 */
export function removeEventHandlers(html) {
  const virtualDOM = document.createElement('div');
  virtualDOM.innerHTML = html;

  // Remove all inline event handler attributes (onclick, onerror, onload, etc.)
  const allElements = virtualDOM.querySelectorAll('*');
  for (let i = 0; i < allElements.length; i++) {
    const element = allElements[i];
    const attributes = element.attributes;
    // Iterate backwards since we're removing items
    for (let j = attributes.length - 1; j >= 0; j--) {
      const attrName = attributes[j].name.toLowerCase();
      if (attrName.startsWith('on')) {
        element.removeAttribute(attributes[j].name);
      }
    }
  }

  return virtualDOM.innerHTML;
}

/**
 * Remove any elements not present in `ALLOWED_HTML_ELEMENTS`.
 *
 * This is a tag allowlist; it removes the element but preserves its child nodes
 * (so text content is retained).
 *
 * @param {string} html
 * @returns {string}
 */
export function filterAllowedElements(html) {
  const virtualDOM = document.createElement('div');
  virtualDOM.innerHTML = html;

  // First, completely remove script and style tags including their content
  const scriptTags = virtualDOM.querySelectorAll('script');
  for (let i = 0; i < scriptTags.length; i++) {
    scriptTags[i].remove();
  }

  const styleTags = virtualDOM.querySelectorAll('style');
  for (let i = 0; i < styleTags.length; i++) {
    styleTags[i].remove();
  }

  // Process all elements recursively
  function processNode(node) {
    // Process child nodes first (depth-first)
    const childNodes = Array.from(node.childNodes);
    for (let i = 0; i < childNodes.length; i++) {
      const child = childNodes[i];
      if (child.nodeType === Node.ELEMENT_NODE) {
        processNode(child);
      }
    }

    // Check if this is an element node and if it's not allowed
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();
      if (!ALLOWED_HTML_ELEMENTS.has(tagName)) {
        // Create a document fragment to hold the child nodes
        const fragment = document.createDocumentFragment();

        // Move all child nodes to the fragment
        while (node.firstChild) {
          fragment.appendChild(node.firstChild);
        }

        // Replace the disallowed element with its children
        if (node.parentNode) {
          node.parentNode.replaceChild(fragment, node);
        }
      }
    }
  }

  // Process all children of the virtual DOM
  const children = Array.from(virtualDOM.childNodes);
  for (let i = 0; i < children.length; i++) {
    if (children[i].nodeType === Node.ELEMENT_NODE) {
      processNode(children[i]);
    }
  }

  return virtualDOM.innerHTML;
}

/**
 * Sanitize HTML for safe insertion (e.g. via Vue `v-html`).
 *
 * Applies a conservative tag allowlist and strips inline styles + event handler
 * attributes. If you expand the allowlist, be careful to avoid tags/attrs that
 * can introduce script execution (e.g. SVG, links, iframes).
 *
 * @param {string} html
 * @returns {string}
 */
export function sanitizeHtml(html) {
  // Apply all sanitization steps in sequence
  let sanitized = filterAllowedElements(html);
  sanitized = removeInlineStyles(sanitized);
  sanitized = removeEventHandlers(sanitized);
  return sanitized;
}
